import {
    Accordion, AccordionDetails, AccordionSummary,
    Box, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, FormControlLabel, FormGroup, Grid, Switch, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import { Form } from '@components/Form';
import MDBox from '@components/MDBox';
import MDButton from '@components/MDButton';
import { mediumDialog } from 'assets/theme/dialogSize';
import { setLoading, useMaterialUIController } from 'context';
import { ProfileService } from '../services/profile.service';
import { ProfileRequest } from '../services/requests/profile.request';
import { ModuleWithScreens, ProfileResponse } from '../services/responses/profile.response';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { firstErrorMessage } from '@functions/first-error-message';

/// Do mais aberto ao mais fechado; nomes do ELeadVisibility do servidor.
const LEAD_VISIBILITY = ['All', 'OwnAndUnassigned', 'OwnOnly'];

interface Props {
    open: boolean;
    edit: ProfileResponse | null;
    modules: ModuleWithScreens[];
    /// Ações que o servidor conhece, na ordem do enum.
    actions: string[];
    handleModal: () => void;
    getAll: () => void;
    clearSearch: () => void;
}

export const ProfileModal = ({
    open, edit, modules: screenModules, actions: profileActions, handleModal, getAll, clearSearch,
}: Props) => {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert } = useAlert();
    const isMobile: boolean = window.innerWidth <= 768;
    const profileService = new ProfileService();

    const [checked, setChecked] = useState<string[]>([]);
    const [expanded, setExpanded] = useState<string[]>([]);

    const schema = z.object({
        name: z.string().min(1, t('perfis.nomeObrigatorio')).max(120),
        active: z.boolean(),
        leadVisibility: z.string().min(1, t('perfis.escolhaVisibilidade')),
        actions: z.array(z.string()),

        // Perfil sem tela entra e não vê nada: salvar sem aviso criaria usuário quebrado.
        screens: z.array(z.string()).min(1, t('perfis.selecioneAoMenosUmaTela')),
    });

    const form = useForm<ProfileRequest>({
        resolver: zodResolver(schema),
        defaultValues: { name: '', active: true, leadVisibility: 'OwnAndUnassigned', actions: [], screens: [] },
    });

    const { control, handleSubmit, reset, setValue, watch, formState: { errors } } = form;
    const leadVisibility = watch('leadVisibility');

    useEffect(() => {
        if (!open) return;

        reset({
            name: edit?.name ?? '',
            active: edit?.active ?? true,
            // Perfil novo começa sem ação: quem cria escolhe o que liberar.
            leadVisibility: edit?.leadVisibility ?? 'OwnAndUnassigned',
            actions: edit?.actions ?? [],
            screens: edit?.screens ?? [],
        });

        setChecked(edit?.screens ?? []);

        // Abre fechado, como no mgHealth: o checkbox do módulo já mostra o que está marcado.
        setExpanded([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        setValue('screens', checked);
    }, [checked, setValue]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            showAlert(firstErrorMessage(errors) ?? t('general.camposObrigatorios'), 'error', 2500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errors]);

    const toggleAccordion = (screenModule: string) =>
        setExpanded((current) => current.includes(screenModule)
            ? current.filter((x) => x !== screenModule)
            : [...current, screenModule]);

    const toggleScreen = (screen: string, isChecked: boolean) =>
        setChecked((current) => isChecked
            ? [...current, screen]
            : current.filter((x) => x !== screen));

    const toggleModule = (screenModule: ModuleWithScreens, isChecked: boolean) =>
        setChecked((current) => isChecked
            ? [...new Set([...current, ...screenModule.screens])]
            : current.filter((x) => !screenModule.screens.includes(x)));

    const save = async (payload: ProfileRequest) => {
        setLoading(dispatch, true);

        try {
            if (edit) {
                await profileService.update(edit.id, payload);
                showAlert(t('general.atualizacao'), 'success', 2500);
                getAll();
            } else {
                await profileService.create(payload);
                showAlert(t('general.cadastro'), 'success', 2500);
                clearSearch();
            }

            handleModal();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    return (
        <Dialog open={open} onClose={handleModal} fullWidth maxWidth="xl" scroll="paper">
            <DialogTitle>{edit ? t('general.editar') : t('general.adicionar')}</DialogTitle>
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(save)}>
                    <DialogContent style={mediumDialog}>
                        <Grid container spacing={2}>
                            <Form.Field size={isMobile ? 12 : 6}>
                                <FormControl fullWidth>
                                    <Form.Label>
                                        {t('perfis.perfil')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Form.Input
                                        type="text"
                                        autoComplete="off"
                                        size="medium"
                                        name="name"
                                        autoFocus
                                    />
                                    <Box mt={1} />
                                    <Form.ErrorMessage field="name" />
                                </FormControl>
                            </Form.Field>

                            {/* Separado das telas: elas dizem onde a pessoa entra, esta diz quanto ela enxerga. */}
                            <Form.Field size={isMobile ? 12 : 6}>
                                <FormControl fullWidth>
                                    <Form.Label>
                                        {t('perfis.visibilidadeTitulo')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Form.AutoCompleteSelect
                                        name="leadVisibility"
                                        options={LEAD_VISIBILITY.map((level) => ({
                                            value: level,
                                            label: t(`perfis.visibilidade.${level}`),
                                        }))}
                                    />
                                    {leadVisibility && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {t(`perfis.visibilidadeAjuda.${leadVisibility}`)}
                                        </Typography>
                                    )}
                                    <Form.ErrorMessage field="leadVisibility" />
                                </FormControl>
                            </Form.Field>

                            {/* As telas dizem onde a pessoa entra; as ações, o que ela pode mexer lá dentro. */}
                            <Form.Field size={12}>
                                <Typography>{t('perfis.acoesTitulo')}</Typography>
                                <Controller
                                    name="actions"
                                    control={control}
                                    render={({ field }) => (
                                        // Duas por linha: quatro switches empilhados ocupavam a tela à toa.
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                columnGap: 3,
                                                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                            }}
                                        >
                                            {profileActions.map((action) => (
                                                <FormControlLabel
                                                    key={action}
                                                    sx={{ alignItems: 'flex-start', mb: 1 }}
                                                    control={
                                                        <Switch
                                                            checked={field.value.includes(action)}
                                                            onChange={(e) => field.onChange(e.target.checked
                                                                ? [...field.value, action]
                                                                : field.value.filter((x) => x !== action))}
                                                            color="info"
                                                        />
                                                    }
                                                    label={
                                                        <Box sx={{ pt: 1 }}>
                                                            <Typography variant="body2">
                                                                {t(`perfis.acoes.${action}`)}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {t(`perfis.acoesAjuda.${action}`)}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            ))}
                                        </Box>
                                    )}
                                />
                            </Form.Field>

                            <Form.Field size={12}>
                                <Typography>
                                    {t('perfis.modulos')}
                                    <Form.MandatoryIcon />
                                </Typography>
                                <Form.ErrorMessage field="screens" />
                            </Form.Field>

                            {/* Grade simples dentro do Field: alinha à esquerda com o título e os switches. */}
                            <Form.Field size={12}>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gap: 2,
                                        gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
                                        alignItems: 'start',
                                    }}
                                >
                                    {screenModules.map((screenModule) => {
                                        const all = screenModule.screens
                                            .every((screen) => checked.includes(screen));

                                        const someChecked = !all && screenModule.screens
                                            .some((screen) => checked.includes(screen));

                                        return (
                                            <Accordion key={screenModule.module} expanded={expanded.includes(screenModule.module)}>
                                                <AccordionSummary
                                                    expandIcon={
                                                        <ExpandMoreIcon
                                                            fontSize="large"
                                                            onClick={() =>
                                                                toggleAccordion(screenModule.module)}
                                                        />
                                                    }
                                                >
                                                    <FormGroup>
                                                        <Form.CheckBox
                                                            name={screenModule.module}
                                                            label={t(`modulos.${screenModule.module}`)}
                                                            checked={all}
                                                            indeterminate={someChecked}
                                                            onChange={(_, isChecked) =>
                                                                toggleModule(screenModule, isChecked)}
                                                        />
                                                    </FormGroup>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    {screenModule.screens.map((screen) => (
                                                        <FormGroup key={screen}>
                                                            <Form.CheckBox
                                                                name={screen}
                                                                label={t(`telasDoSistema.${screen}`)}
                                                                checked={checked.includes(screen)}
                                                                onChange={(_, isChecked) =>
                                                                    toggleScreen(screen, isChecked)}
                                                            />
                                                        </FormGroup>
                                                    ))}
                                                </AccordionDetails>
                                            </Accordion>
                                        );
                                    })}
                                </Box>
                            </Form.Field>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        {/* Só na edição: perfil novo nasce ativo, e um interruptor que não obedece seria mentira. */}
                        {edit ? (
                            <Controller
                                name="active"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={Boolean(field.value)}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        }
                                        label={t(field.value ? 'general.ativo' : 'general.inativo')}
                                    />
                                )}
                            />
                        ) : (
                            <Box />
                        )}
                        <MDBox>
                            <MDButton
                                sx={{ marginRight: '5px' }}
                                variant="gradient"
                                color="dark"
                                onClick={handleModal}
                            >
                                {t('general.cancelar')}
                            </MDButton>
                            <MDButton variant="gradient" color="success" type="submit">
                                {t('general.salvar')}
                            </MDButton>
                        </MDBox>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
